import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { HomeService } from '../../core/services/home.service';
import { LanguageService } from '../../core/services/language.service';

interface PolicyForQR {
  policyNo: string;
  applicationNo: string;
  productId: string;
  productName: string;
  insuredName: string;
  status: string;
  icon: string;
}

@Component({
  selector: 'app-policy-qr-selector',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    <div class="modal-overlay" (click)="onClose()">
      <div class="modal-content" (click)="$event.stopPropagation()">
        <!-- <div class="modal-header">
          <button class="close-btn" (click)="onClose()" [attr.aria-label]="'Close'">×</button>
        </div> -->

        <!-- Loading State -->
        @if (isLoading) {
          <div class="loading-state">
            <i class="fas fa-spinner fa-spin"></i>
            <p>{{ 'common.loading' | translate }}</p>
          </div>
        }

        <!-- Policy List -->
        @if (!isLoading && policies.length > 0) {
          <div class="policy-list">
            @for (policy of policies; track policy.policyNo) {
              <button 
                class="policy-item"
                (click)="onSelectPolicy(policy)"
                [attr.aria-label]="'Select policy ' + policy.policyNo">
                <div class="policy-icon">
                  <i [class]="getProductIcon(policy.productId)"></i>
                </div>
                <div class="policy-info">
                  <div class="policy-name">{{ policy.productName }}</div>
                  <div class="policy-number">{{ policy.policyNo }}</div>
                  <div class="policy-insured">{{ policy.insuredName }}</div>
                </div>
                <div class="policy-action">
                  <i class="fas fa-qrcode"></i>
                </div>
              </button>
            }
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.6);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      padding: 20px;
    }

    .modal-content {
      background: white;
      border-radius: 16px;
      width: 100%;
      max-width: 500px;
      max-height: 80vh;
      display: flex;
      flex-direction: column;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
    }

    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 20px;
      border-bottom: 1px solid #e5e7eb;
    }

    .modal-header h3 {
      margin: 0;
      font-size: 20px;
      color: #1f2937;
      font-family: 'boonhome', sans-serif;
    }

    .close-btn {
      background: none;
      border: none;
      font-size: 32px;
      color: #9ca3af;
      cursor: pointer;
      padding: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: color 0.2s;
    }

    .close-btn:hover {
      color: #374151;
    }

    .loading-state,
    .empty-state,
    .error-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 60px 20px;
      color: #6b7280;
    }

    .loading-state i,
    .empty-state i,
    .error-state i {
      font-size: 48px;
      margin-bottom: 16px;
      color: #9ca3af;
    }

    .loading-state i {
      color: #9e1c20;
    }

    .error-state i {
      color: #ef4444;
    }

    .btn-retry {
      margin-top: 16px;
      padding: 10px 24px;
      background: #9e1c20;
      color: white;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      transition: background 0.2s;
    }

    .btn-retry:hover {
      background: #c62828;
    }

    .policy-list {
      overflow-y: auto;
      padding: 16px;
      max-height: calc(80vh - 80px);
      margin-top: 12px
    }

    .policy-item {
      display: flex;
      align-items: center;
      gap: 16px;
      width: 100%;
      padding: 16px;
      background: #f9fafb;
      border: 2px solid #e5e7eb;
      border-radius: 12px;
      margin-bottom: 12px;
      cursor: pointer;
      transition: all 0.2s;
      text-align: left;
    }

    .policy-item:hover {
      background: #f3f4f6;
      border-color: #9e1c20;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(158, 28, 32, 0.15);
    }

    .policy-icon {
      width: 48px;
      height: 48px;
      background: linear-gradient(135deg, #9e1c20 0%, #c62828 100%);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .policy-icon i {
      font-size: 24px;
      color: white;
    }

    .policy-info {
      flex: 1;
      min-width: 0;
    }

    .policy-name {
      font-weight: 600;
      font-size: 16px;
      color: #1f2937;
      margin-bottom: 4px;
      font-family: 'boonhome', sans-serif;
    }

    .policy-number {
      font-size: 13px;
      color: #6b7280;
      font-family: monospace;
      margin-bottom: 2px;
    }

    .policy-insured {
      font-size: 13px;
      color: #9ca3af;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .policy-action {
      flex-shrink: 0;
    }

    .policy-action i {
      font-size: 24px;
      color: #9e1c20;
    }
  `]
})
export class PolicyQrSelectorComponent implements OnInit {
  @Output() policySelected = new EventEmitter<PolicyForQR>();
  @Output() closed = new EventEmitter<void>();

  policies: PolicyForQR[] = [];
  isLoading = false;
  errorMessage = '';

  constructor(
    private homeService: HomeService,
    private languageService: LanguageService
  ) {}

  ngOnInit() {
    this.loadPolicies();
  }

  loadPolicies() {
    this.isLoading = true;
    this.errorMessage = '';

    // Get user phone from session
    const userPhone = sessionStorage.getItem('ih_user_phone');
    
    if (!userPhone) {
      this.errorMessage = 'User not authenticated. Please reload the app from INSEE Hub.';
      this.isLoading = false;
      this.policies = []; // Clear any existing policies
      return;
    }

    this.homeService.getDashboard(userPhone).subscribe({
      next: (dashboard: any) => {
        this.policies = dashboard.policies
          .filter((p: any) => p.policyStatusId !== 3) // Exclude terminated policies
          .map((p: any) => ({
            policyNo: p.policyNo,
            applicationNo: p.applicationNo,
            productId: p.productId,
            productName: this.getProductName(p),
            insuredName: this.getInsuredName(p),
            status: p.policyStatus || '',
            icon: this.getProductIcon(p.productId)
          }));
        this.isLoading = false;
      },
      error: (error: any) => {
        console.error('Error loading policies:', error);
        this.errorMessage = 'Failed to load policies. Please try again.';
        this.isLoading = false;
        this.policies = []; // Clear policies on error
      }
    });
  }

  getProductName(policy: any): string {
    const lang = this.languageService.currentLanguage();
    return lang === 'lo' ? policy.descLa : policy.descEn || policy.descLa;
  }

  getInsuredName(policy: any): string {
    const lang = this.languageService.currentLanguage();
    if (lang === 'lo') {
      return `${policy.insuredNameLa || ''} ${policy.insuredSurnameLa || ''}`.trim();
    }
    return `${policy.insuredNameEn || ''} ${policy.insuredSurnameEn || ''}`.trim();
  }

  getProductIcon(productId: string): string {
    const iconMap: { [key: string]: string } = {
      'SV': 'fas fa-piggy-bank',
      'WL': 'fas fa-shield-alt',
      'RT': 'fas fa-user-shield',
      'ED': 'fas fa-graduation-cap',
      'CI': 'fas fa-heartbeat'
    };
    return iconMap[productId] || 'fas fa-file-contract';
  }

  onSelectPolicy(policy: PolicyForQR) {
    this.policySelected.emit(policy);
  }

  onClose() {
    this.closed.emit();
  }
}
